import {
	IAuPaginated,
	IAuPaginationData,
	PaginationPageInfo,
} from '@shared/ui-components/au-paginator/pagination-page.info';

import { IRequest } from '@app/pages/cmms/interfaces/request.interfaces';
import { IAuFileMetadata } from '@shared/ui-components/au-file-loader/au-file-loader.interfaces';
import { IIncident } from '@app/pages/cmms/interfaces/incidents.interface';
import { IAttachedFile, IFileEntity } from '@app/pages/cmms/interfaces/files.interfaces';
import { IClassification } from '@app/pages/cmms/interfaces/classification.interfaces';
import {
	ConsequenceRatingChoicesType,
	CostAllocationType,
	RiskLevelType,
	TimeframeType,
} from '@app/pages/cmms/configs/cmms-maintenance.enums';
import { ILabel } from '@app/pages/settings/interfaces/labels.interfaces';
import { UsersTeamPermissionLocationType } from '@app/pages/settings/users/interfaces/users-management.interfaces';
import { IWorkOrder } from '@app/domain/cmms-types/workorder-types';

export interface ICmmsAssetQueryVariables extends IAuPaginationData {
	ifcIdExpressId?: string;
	ifcIdModelId?: string;
	locationId?: string;
	locations?: string[];
	pkIn?: string;
	search?: string;
	typesIn?: string;
	classificationsAsset?: string[];
	includeFromIfc?: boolean;
	sortBy?: string;
	name_Iexact?: string;
	conditionGrade?: number;
	conditionGrade_In?: string[];
}

export interface IIfcId {
	expressId: string;
	modelId: string;
	globalId: string;
}

export interface ICmmsAsset extends IAssetMaintenancePayload {
	pk: string;
	name: string;
	type?: IAssetTypeObjectType;
	ifcId: IIfcId;
	servicedBy: {
		id: string;
		name: string;
		type: string;
	}[];
	incidents: Partial<IIncident>[];
	workOrders: Partial<IWorkOrder>[];
	requests: Partial<IRequest>[];
	manufacturedBy: {
		id: string;
		name: string;
		type: string;
	};
	location: ILocationAssetInput;
	tags: any;
	classificationAttachedFiles: IAttachedFile[];
	classificationAttachedFileEntities: {
		pageInfo: PaginationPageInfo;
		totalCount: number;
		classificationAttachedFileEntities: IAttachedFile[];
	};
	assetAttachedFiles: IAttachedFile[];
	classifications: IClassification[];
	description: string;
	files: Partial<IFileEntity> & Pick<IFileEntity, 'pk'>[];
	coordinates?: {
		x: number;
		y: number;
		z: number;
	};
	conditionGrade?: number;
	conditionGradeNote?: string;
	productName?: string;
	productYear?: Date | string;
	serialNumber?: string;
	installationDate?: string;
	installationCost?: number;
	warranty: string;
	labels: ILabel[];
	locationScreenshotUrl?: string | null;
}

// TODO createVariables and editPayload are nearly the same.
//  We can extract it to common interface and extend from it
//  to not duplicate same fields multiple time.
//  Also can be changed into Partial type to remove the need in ? signs
export interface ICmmsAssetCreateVariables extends IAssetMaintenancePayload {
	name: string;
	ifcId?: IIfcId | object;
	customTags?: string[];
	classifications?: string[];
	location?: {
		id: string;
		type: UsersTeamPermissionLocationType;
	};
	manufacturedById?: string;
	servicedBy?: string[];
	description?: string;
	coordinates?: ICoordinatesInput;
	conditionGrade?: number;
	conditionGradeNote?: string;
	productName?: string;
	productYear?: Date | string;
	serialNumber?: string;
	installationDate?: string;
	installationCost?: number;
	warranty?: string;
	labels?: string[];
}

export interface ICmmsAssetEntityToAttach {
	files?: { pk: string; name: string; file: IAuFileMetadata; importConfirmed: boolean }[];
	workOrders?: Partial<IWorkOrder>[];
	incident?: Partial<IIncident>[];
	requests?: Partial<IRequest>[];
	classifications?: Pick<IClassification, 'pk'>[];
}

export interface ICmmsAssetEditPayload
	extends IAssetMaintenancePayload,
		IAssetCostOfRepairFilterValues {
	pk: string;
	servicedBy?: string[];
	customTags?: string[];
	ifcId?: IIfcId;
	location?: {
		id: string;
		type: UsersTeamPermissionLocationType;
	};
	classifications?: string[];
	manufacturedById?: string;
	name?: string;
	description?: string;
	coordinates?: ICoordinatesInput;
	conditionGrade?: number;
	conditionGradeNote?: string;
	productName?: string;
	matterportUrl?: string;
	productYear?: Date | string;
	serialNumber?: string;
	installationDate?: string;
	installationCost?: number;
	warranty?: string;
	labels?: string[];
}

export interface ICmmsAssetPaginatedData {
	assets: IAuPaginated<ICmmsAsset>;
}

export interface IDtAsset {
	pk: string;
	name: string;
	coordinates: {
		x: string;
		y: string;
		z: string;
	};
	type: {
		name: string;
	};
}

export interface IDtAssetPaginatedData {
	assets: IAuPaginated<IDtAsset>;
}

export enum AssetMappingEntityTypes {
	Classification = 'Classification',
	FileEntity = 'FileEntity',
	Incident = 'Incident',
	Request = 'Request',
	WorkOrder = 'WorkOrder',
}

export interface IAssetTypeObjectType {
	created: string;
	id: string;
	ifcType: string;
	name: string;
	pk: string;
	updated: string;
}

export enum assetsObjectTypes {
	incidents = 'incidents',
	requests = 'requests',
	workOrders = 'workOrders',
	files = 'files',
}

export interface ILinkAssetsObjectsVariables {
	assets: string[];
	classifications?: string[];
	files?: string[];
	incidents?: string[];
	location?: string;
	requests?: string[];
	workOrders?: string[];
}

export interface IUnlinkAssetObjects {
	pk: string;
	files?: string[];
	incidents?: string[];
	requests?: string[];
	workOrders?: string[];
}

export interface IBatchUpdateAssetPayload {
	assets: string[];
	classification?: string;
	location?: string;
}

export interface ICoordinatesInput {
	x: number;
	y: number;
	z: number;
}

export interface IAssetMaintenancePayload {
	timeframe?: TimeframeType;
	costAllocation?: CostAllocationType;
	costOfRepair?: string;
	consequenceRating?: ConsequenceRatingChoicesType;
	riskLevel?: RiskLevelType;
	deviationType?: string;
	matterportUrl?: string;
}

export interface IAssetCostOfRepairFilterValues {
	costOfRepair?: string;
	costOfRepair_Gt?: string;
	costOfRepair_Gte?: string;
	costOfRepair_Isnull?: boolean;
	costOfRepair_Lt?: string;
	costOfRepair_Lte?: string;
}

export interface ILocationAssetInput {
	id: string;
	name: string;
	type: string;
	__typename: string;
}
